/* eslint-disable max-lines */
/* eslint-disable complexity */
import { Injectable } from '@angular/core';
import { StorageValue } from '../data-models/common.interfaces';
import { LoggingService } from '../logging/logging.service';

/**
 * Service for storing data as key value pairs.
 */
@Injectable({
  providedIn: 'root'
})
export class StorageService {
  /**
   * The constructor for the storage service.
   */
  constructor(private logging: LoggingService) {}

  /**
   * Get item from localStorage and parse it.
   */
  get(key: string, bypass = false, useSessionStorage = false) {
    try {
      if (useSessionStorage) {
        return window.JSON.parse(window.sessionStorage.getItem(key) as string);
      } else {
        const result = window.JSON.parse(
          window.localStorage.getItem(key) as string
        );

        if (result) {
          if (bypass) {
            return result.value;
          }
          if (result.timeout >= new Date().getTime() || result.timeout === 0) {
            return result.value;
          }
          return null;
        }

        if (!result) {
          const cookieResult = this.getCookie(key);
          if (cookieResult) {
            return cookieResult;
          }
        }

        return result;
      }
    } catch (e) {
      const value = this.getCookie(key);
      this.logging.log(e as string);

      if (value) {
        return value;
      }
      return null;
    }
  }

  /**
   * Stringify data and save to localStorage.
   */
  set(
    key: string,
    value: StorageValue,
    timeout = 0,
    useSessionStorage = false
  ) {
    try {
      if (useSessionStorage) {
        window.sessionStorage.setItem(key, window.JSON.stringify(value));
      } else {
        const result = {
          value,
          timeout
        };

        if (timeout) {
          const timeoutDateObject: Date = new Date();
          timeoutDateObject.setMilliseconds(
            new Date().getMilliseconds() + timeout
          );
          result.timeout = timeoutDateObject.getTime();
        }

        window.localStorage.setItem(key, window.JSON.stringify(result));
      }
    } catch (e) {
      this.setCookie(key, value, '/', window.location.hostname, timeout);
      this.logging.log(e as string);
    }
  }

  /**
   * Remove item from localStorage.
   */
  remove(key: string, useSessionStorage = false) {
    try {
      if (useSessionStorage) {
        window.sessionStorage.removeItem(key);
      } else {
        window.localStorage.removeItem(key);
      }
    } catch (exception) {
      this.logging.log(exception as string);
      this.unsetCookie(key);
    }
  }

  /**
   * Get a cookie's value.
   */
  getCookie(cookieName: string): string {
    if (cookieName) {
      const name = cookieName.toLowerCase();
      const crumbles = window.document.cookie.split(';');
      const len = crumbles.length;

      for (let i = 0; i < len; i++) {
        const pair = crumbles[i].split('=');
        const key = decodeURIComponent(pair[0].trim().toLowerCase());
        const val = pair.length > 1 ? pair[1] : '';

        if (name === key) {
          return decodeURIComponent(val);
        }
      }
    }
    return '';
  }

  /**
   * Parses Cookie value
   */
  getParsedCookie<T>(cookieName: string): T {
    try {
      return JSON.parse(this.getCookie(cookieName)) as T;
    } catch (exception) {
      return exception as T;
    }
  }

  /**
   *  Set a cookie.
   */
  setCookie(
    key: string,
    value: StorageValue = '',
    path = '/',
    domain: string = window.location.hostname,
    ttl = 0
  ): string {
    const cookieValue = JSON.stringify(value);
    const cookie = [
      key + '=' + escape(cookieValue),
      'path=' + path,
      'domain=' + domain
    ];
    const expiryTime = ttl === 0 ? ttl : (ttl / 1000) * 60 * 60;
    cookie.push('max-age=' + expiryTime);
    return (window.document.cookie = cookie.join('; ')) ?? '';
  }

  /**
   * Unset Cookie.
   */
  unsetCookie(key: string): boolean {
    if (this.getCookie(key)) {
      this.setCookie(key, '', '/', '.delta.com', 0);
      return true;
    }
    return false;
  }

  /**
   * Get the selected language from the Cookie name passed.
   */
  getSelectedLanguage(cookieName: string, cookieValue = false): string {
    let value = '';
    if (cookieName) {
      const prefCookieValue = this.getCookie(cookieName)
        ? this.getCookie(cookieName)
        : 'en-us';
      if (cookieValue) {
        value = prefCookieValue;
      } else {
        const prefCookieSplit = prefCookieValue.split('-');
        const languageSelected =
          prefCookieSplit && prefCookieSplit[0] ? prefCookieSplit[0] : 'en';
        value = languageSelected.toLowerCase();
      }
    }
    return value;
  }
}
